import { useState, useEffect } from "react";
import { clientLogger } from "@/lib/logger";
import { getConfig } from "@/app/product/_actions/config.actions";
import { useAppData, useAppStore } from "@/store/appStore";
import { FreebieProduct } from "@/@types/freebies";

// Data Layer - API and validation is now handled in the useEffect directly

// Business Logic Layer - Parsers and transformers
const parseShowFlag = (showValue: unknown): boolean => {
	if (typeof showValue === "boolean") {
		return showValue;
	}

	if (typeof showValue === "string") {
		return showValue.toLowerCase() !== "false";
	}

	return true; // Default to true if value is undefined or other type
};

// Opt-in counterpart to parseShowFlag: anything other than an explicit
// true / "true" (including an absent value) means the feature is off.
const parseOptInFlag = (value: unknown): boolean =>
	value === true || String(value).toLowerCase() === "true";

// When a subtype config object exists, absent keys default to false (whitelist semantics).
// When there is no subtype config at all, all subtypes are allowed (return true).
const parseSubtypeFlag = (
	subtypeObj: Record<string, unknown> | undefined | null,
	key: string,
): boolean => {
	if (!subtypeObj) return true;
	const val = subtypeObj[key];
	if (val === undefined || val === null) return false;
	return parseShowFlag(val);
};

const extractTenureConfig = (
	tenureConfig: TenureConfig,
): {
	allowedTenures: number[];
	defaultTenure: number;
} => {
	const allowedTenures: number[] = Array.isArray(tenureConfig?.allowed)
		? tenureConfig.allowed.map((tenure: number | string) => Number(tenure))
		: [];

	const configDefaultTenure: number = Number(tenureConfig?.default);

	// Validate that default tenure exists in allowed tenures
	const defaultTenure =
		typeof configDefaultTenure === "number" &&
		allowedTenures.includes(configDefaultTenure)
			? configDefaultTenure
			: allowedTenures[allowedTenures.length - 1];

	return { allowedTenures, defaultTenure };
};

const parseConfigData = (
	programs: {
		car: ConfigProgram;
		device: ConfigProgram;
		all?: { terms?: { showctc?: unknown; delivertoofficeaddress?: unknown } };
	},
	consent?: {
		isonboardingconsentenabled?: string;
		isaddressconsentenabled?: string;
	},
	companyLogo?: string,
): ParsedConfig => {
	const carConfig = programs.car;
	const deviceConfig = programs.device;
	const consentConfig = consent || {};
	const showCtc = parseShowFlag(programs.all?.terms?.showctc);
	// Opt-in flag: unlike the other config booleans this defaults to OFF, so an
	// absent value must not be read as enabled.
	const deliverToOfficeAddress = parseOptInFlag(
		programs.all?.terms?.delivertoofficeaddress,
	);

	const showCar = parseShowFlag(carConfig?.show);
	const showDevice = parseShowFlag(deviceConfig?.show);

	const deviceSubtypes = {
		mobile: parseSubtypeFlag(
			deviceConfig?.subtype as Record<string, unknown>,
			"mobile",
		),
		tablet: parseSubtypeFlag(
			deviceConfig?.subtype as Record<string, unknown>,
			"tablet",
		),
		laptop: parseSubtypeFlag(
			deviceConfig?.subtype as Record<string, unknown>,
			"laptop",
		),
	};

	// Helper function to parse consent flag from string value
	const parseConsentFlag = (value: string | undefined): boolean => {
		if (value === undefined || value === null) {
			return false; // Default to disabled
		}

		// Handle string values ("true", "false", etc.)
		return value.toLowerCase() === "true";
	};

	// Extract consent flags from lowercase property names, defaulting to true if not provided
	const isOnboardingConsentEnabled = parseConsentFlag(
		consentConfig.isonboardingconsentenabled,
	);

	const isAddressConsentEnabled = parseConsentFlag(
		consentConfig.isaddressconsentenabled,
	);

	const { allowedTenures: tenuresCar, defaultTenure: defaultCarTenure } =
		extractTenureConfig(
			carConfig?.terms?.eligibility?.leasetenure as TenureConfig,
		);

	const { allowedTenures: tenuresDevice, defaultTenure: defaultDeviceTenure } =
		extractTenureConfig(
			deviceConfig?.terms?.eligibility?.leasetenure as TenureConfig,
		);

	const restrictDeviceCtcView =
		deviceConfig?.restrictctcview != null
			? parseShowFlag(deviceConfig.restrictctcview)
			: false;

	const financeChargesEnabled = parseShowFlag(
		deviceConfig?.terms?.financecharges?.enabled,
	);

	const buybackConfig = ((): BuybackConfig | null => {
		const buybackTerms = deviceConfig?.terms?.buyback;
		if (!buybackTerms) return null;
		const enabled = parseShowFlag(buybackTerms.enabled);
		if (!enabled) return null;

		// eslint-disable-next-line @typescript-eslint/no-explicit-any
		const rawRates: any[] = Array.isArray(buybackTerms.rates)
			? buybackTerms.rates
			: buybackTerms.rates && typeof buybackTerms.rates === "object"
				? Object.values(buybackTerms.rates)
				: [];
		// eslint-disable-next-line @typescript-eslint/no-explicit-any
		const rawDeductions: any[] = Array.isArray(buybackTerms.deduction)
			? buybackTerms.deduction
			: buybackTerms.deduction && typeof buybackTerms.deduction === "object"
				? Object.values(buybackTerms.deduction)
				: [];
		const rates: BuybackRate[] = rawRates
			.filter((r) => r && typeof r.label === "string" && r.rate != null)
			.map((r) => ({
				label: r.label as string,
				min: Number(r.min ?? 0),
				max: Number(r.max ?? 0),
				rate: Number(r.rate),
			}));
		const deductions: BuybackDeduction[] = rawDeductions
			.filter(
				(d) => d && typeof d.label === "string" && typeof d.amount === "string",
			)
			.map((d) => ({
				label: d.label as string,
				amount: d.amount as string,
			}));
		const fee = buybackTerms.price != null ? Number(buybackTerms.price) : 0;
		return { enabled, rates, deductions, fee };
	})();

	const fuelDriverMaintenance = ((): FuelDriverMaintenance | null => {
		const raw = carConfig?.terms?.fueldrivermaintenance;
		if (!raw) return null;

		const parseEnabled = (v: unknown): boolean =>
			typeof v === "boolean"
				? v
				: typeof v === "string"
					? v.toLowerCase() === "true"
					: false;

		const parseAmount = (v: unknown): number => {
			const n = Number(v);
			return Number.isFinite(n) ? n : 0;
		};

		const fuelExpense = raw.fuelexpense
			? (() => {
					const rawFuel = raw.fuelexpense as Record<string, unknown>;
					const rawFuelTypes = rawFuel.fueltypes ?? rawFuel.fuelTypes;
					return {
						enabled: parseEnabled(rawFuel.enabled),
						fuelTypes: Array.isArray(rawFuelTypes)
							? rawFuelTypes
									.filter((t): t is string => typeof t === "string")
									.map((t) => t.toLowerCase())
							: undefined,
						maxAmount: parseAmount(rawFuel.maxamount ?? rawFuel.maxAmount),
					};
				})()
			: undefined;

		const driverExpense = raw.driverexpense
			? (() => {
					const rawDriver = raw.driverexpense as Record<string, unknown>;
					return {
						enabled: parseEnabled(rawDriver.enabled),
						maxAmount: parseAmount(rawDriver.maxamount ?? rawDriver.maxAmount),
					};
				})()
			: undefined;

		const maintenanceExpense = raw.maintenanceexpense
			? (() => {
					const rawMaint = raw.maintenanceexpense as Record<string, unknown>;
					return {
						enabled: parseEnabled(rawMaint.enabled),
						maxAmount: parseAmount(rawMaint.maxamount ?? rawMaint.maxAmount),
					};
				})()
			: undefined;

		if (!fuelExpense && !driverExpense && !maintenanceExpense) return null;
		return { fuelExpense, driverExpense, maintenanceExpense };
	})();

	const availableInsuranceYears = ((): number[] => {
		const insurancePct = deviceConfig?.terms?.insurance_percentage;
		// Plain string format: "10" — single year only
		if (insurancePct && typeof insurancePct === "string") {
			return [1];
		}
		if (
			insurancePct &&
			typeof insurancePct === "object" &&
			// eslint-disable-next-line @typescript-eslint/no-explicit-any
			String((insurancePct as any).multi_year_enabled).toLowerCase() === "true"
		) {
			// eslint-disable-next-line @typescript-eslint/no-explicit-any
			const rawRates = (insurancePct as any).rates ?? [];
			let years: number[];
			if (Array.isArray(rawRates)) {
				// Array format: [rate1, rate2, ...] where index 0 = year 1
				years = (rawRates as (number | string | null)[])
					.map((rate, i) => (rate != null ? i + 1 : null))
					.filter((y): y is number => y !== null)
					.sort((a, b) => a - b);
			} else {
				// Legacy object format: {"1": rate1, "2": rate2, ...}
				years = Object.keys(rawRates)
					.map(Number)
					.filter((i) => i > 0)
					.sort((a, b) => a - b);
			}
			return years;
		}
		return [1];
	})();

	const insuranceRates = ((): (number | null)[] => {
		const insurancePct = deviceConfig?.terms?.insurance_percentage;
		// Plain string format: "10" — single year rate
		if (insurancePct && typeof insurancePct === "string") {
			const n = Number(insurancePct);
			return Number.isFinite(n) ? [n] : [];
		}
		if (insurancePct && typeof insurancePct === "object") {
			// eslint-disable-next-line @typescript-eslint/no-explicit-any
			const rawRates = (insurancePct as any).rates;
			if (Array.isArray(rawRates)) {
				// Array format: [rate1, rate2, ...] where index 0 = year 1
				return (rawRates as (number | string | null)[]).map((rate) => {
					if (rate == null) return null;
					const numericRate = Number(rate);
					return Number.isFinite(numericRate) ? numericRate : null;
				});
			} else if (rawRates && typeof rawRates === "object") {
				// Legacy object format: {"1": rate1, "2": rate2, ...} — convert to 0-indexed array
				const maxKey = Math.max(
					...Object.keys(rawRates).map(Number).filter(Number.isFinite),
				);
				if (maxKey > 0) {
					const arr: (number | null)[] = [];
					for (let i = 1; i <= maxKey; i++) {
						const v = rawRates[String(i)];
						const n = Number(v);
						arr.push(Number.isFinite(n) ? n : null);
					}
					return arr;
				}
			}
		}
		return [];
	})();

	const allowedAccessoryTypes: string[] = deviceConfig?.accessory
		? Object.entries(deviceConfig.accessory)
				.filter(([, v]) => parseShowFlag(v))
				.map(([k]) => k)
		: [];

	const allowedAccessoryCount: number | null = (() => {
		const raw = deviceConfig?.allowedaccessorycount;
		if (raw == null) return null;
		const n = Number(raw);
		return Number.isFinite(n) ? n : null;
	})();

	// Homepage banner — text is per-program, logo is top-level (shared across tabs)
	const parseHomepageBanner = (
		// eslint-disable-next-line @typescript-eslint/no-explicit-any
		raw: any,
	): HomepageBanner | null => {
		const logo = companyLogo || "";
		const text =
			raw && typeof raw === "object" && typeof raw.text === "string"
				? raw.text
				: "";
		if (!logo && !text) return null;
		return { logo, text };
	};
	const homepageBannerCar = parseHomepageBanner(
		(carConfig as Record<string, unknown> | undefined)?.homepage,
	);
	const homepageBannerDevice = parseHomepageBanner(
		(deviceConfig as Record<string, unknown> | undefined)?.homepage,
	);

	return {
		showCar,
		showDevice,
		deviceSubtypes,
		tenuresCar,
		tenuresDevice,
		defaultCarTenure,
		defaultDeviceTenure,
		isOnboardingConsentEnabled,
		isAddressConsentEnabled,
		companyBanners: [],
		restrictDeviceCtcView,
		financeChargesEnabled,
		showCtc,
		deliverToOfficeAddress,
		buybackConfig,
		fuelDriverMaintenance,
		availableInsuranceYears,
		insuranceRates,
		allowedAccessoryTypes,
		allowedAccessoryCount,
		homepageBannerCar,
		homepageBannerDevice,
	};
};

// Business Logic Layer - Tenure selection
const getBestTenure = (
	currentTenure: number | undefined,
	allowedTenures: number[],
	defaultTenure: number,
): number => {
	if (!currentTenure && allowedTenures.includes(defaultTenure)) {
		return defaultTenure;
	}

	if (
		currentTenure &&
		!allowedTenures.includes(currentTenure) &&
		allowedTenures.includes(defaultTenure)
	) {
		return defaultTenure;
	}

	return allowedTenures[allowedTenures.length - 1];
};

// const calculateTenureUpdates = (
// 	appData: AppData,
// 	tenuresCar: number[],
// 	tenuresDevice: number[],
// 	defaultCarTenure: number,
// 	defaultDeviceTenure: number
// ) => {
// 	const updates: Record<string, number> = {};

// 	const bestCarTenure = getBestTenure(
// 		appData?.selectedCarTenure,
// 		tenuresCar,
// 		defaultCarTenure
// 	);
// 	if (bestCarTenure !== appData?.selectedCarTenure) {
// 		updates.selectedCarTenure = bestCarTenure;
// 	}

// 	const bestDeviceTenure = getBestTenure(
// 		appData?.selectedDeviceTenure,
// 		tenuresDevice,
// 		defaultDeviceTenure
// 	);
// 	if (bestDeviceTenure !== appData?.selectedDeviceTenure) {
// 		updates.selectedDeviceTenure = bestDeviceTenure;
// 	}

// 	return updates;
// };

const calculateTenureUpdates = (
	selectedCarTenure: number,
	selectedDeviceTenure: number,
	tenuresCar: number[],
	tenuresDevice: number[],
	defaultCarTenure: number,
	defaultDeviceTenure: number,
) => {
	const updates: Record<string, number> = {};

	// Only update car tenure if user hasn't selected OR their selection is invalid
	if (!selectedCarTenure || !tenuresCar.includes(selectedCarTenure)) {
		const bestCarTenure = getBestTenure(
			selectedCarTenure,
			tenuresCar,
			defaultCarTenure,
		);
		updates.selectedCarTenure = bestCarTenure;
	}

	// Only update device tenure if user hasn't selected OR their selection is invalid
	if (!selectedDeviceTenure || !tenuresDevice.includes(selectedDeviceTenure)) {
		const bestDeviceTenure = getBestTenure(
			selectedDeviceTenure,
			tenuresDevice,
			defaultDeviceTenure,
		);
		updates.selectedDeviceTenure = bestDeviceTenure;
	}

	return updates;
};
// Fallback configuration
const getFallbackConfig = () => ({
	showCar: true,
	showDevice: true,
	showToggle: true,
	deviceSubtypes: { mobile: true, tablet: true, laptop: true },
	defaultCarTenure: undefined,
	defaultDeviceTenure: undefined,
	tenuresCar: [],
	tenuresDevice: [],
	isOnboardingConsentEnabled: false,
	isAddressConsentEnabled: false,
	companyBanners: [] as BannerItem[],
	restrictDeviceCtcView: false,
	financeChargesEnabled: true,
	showCtc: true,
	deliverToOfficeAddress: false,
	buybackConfig: null as BuybackConfig | null,
	fuelDriverMaintenance: null as FuelDriverMaintenance | null,
	availableInsuranceYears: [1] as number[],
	insuranceRates: [] as (number | null)[],
	allowedAccessoryTypes: [] as string[],
	allowedAccessoryCount: null as number | null,
	homepageBannerCar: null as HomepageBanner | null,
	homepageBannerDevice: null as HomepageBanner | null,
});

let hasAnyInstanceFetchedSuccessfully = false;

// `useConfig` is called from many independent components (nav, homepage sections,
// product pages, onboarding steps, etc). Without this cache, each mounts its own
// fetch of identical, request-scoped data - confirmed on staging as 5 separate
// `/config` calls within a single homepage load. This module-level cache ensures
// only the first caller for a given auth state actually fetches; every other
// concurrent/subsequent caller (same auth state) awaits that same in-flight or
// already-resolved promise instead of starting a new request. Mirrors the
// `guestLoginPromiseMap` dedup pattern already used in src/lib/server/guest-auth.ts.
let sharedConfigFetch: { authKey: string; promise: Promise<void> } | null =
	null;

const getConfigAuthKey = (
	authorization: string | undefined,
	user: User | null | undefined,
): string => `${authorization ?? ""}:${user?.id ?? ""}`;

// Main Hook - Orchestration layer
export const useConfig = () => {
	const [appData, setData, setCookielessData, hasHydrated] = useAppData();
	const [isConfigReady, setIsConfigReady] = useState(false);

	useEffect(() => {
		const authKey = getConfigAuthKey(appData?.authorization, appData?.user);

		const fetchConfig = async () => {
			try {
				// 1. Fetch data
				const configRes = await getConfig();

				if (!configRes.success || !configRes.response?.programs) {
					throw new Error("Config API returned unsuccessful response");
				}

				const programs = configRes.response.programs;
				const consent = configRes.response.consent;
				// eslint-disable-next-line @typescript-eslint/no-explicit-any
				const rawFreebieConfig = (configRes.response as any).freebieConfig as
					| {
							default: string | string[] | null;
							defaultFreebies: FreebieProduct[];
							rules?: { accessoryCount?: number; minCartValue?: number };
					  }
					| undefined;
				// eslint-disable-next-line @typescript-eslint/no-explicit-any
				const rawBannersSource = (configRes.response as any).banners;
				// Handle both array format [...] and numeric-key object {"0":{...},"1":{...}}
				// (Prisma/PostgreSQL JSONB sometimes serialises JS arrays as objects with string-numeric keys)
				// eslint-disable-next-line @typescript-eslint/no-explicit-any
				const rawBanners: any[] = Array.isArray(rawBannersSource)
					? rawBannersSource
					: rawBannersSource && typeof rawBannersSource === "object"
						? Object.values(rawBannersSource)
						: [];
				// Normalize keys — leaseTerm JSON may use "url" or "webUrl" interchangeably
				const banners: BannerItem[] = rawBanners
					// eslint-disable-next-line @typescript-eslint/no-explicit-any
					.map((b: any) => ({
						webUrl: b.webUrl || b.weburl || b.web_url || b.url || "",
						mobileUrl: b.mobileUrl || b.mobileurl || b.mobile_url || undefined,
						cta: b.cta || b.ctaUrl || b.cta_url || undefined,
						showFor: b.showFor || b.showfor || b.show_for || undefined,
					}))
					.filter((b: BannerItem) => !!b.webUrl);

				clientLogger.info("Config api data", { programs, consent, banners });

				// 2. Parse and transform data
				const {
					showCar,
					showDevice,
					deviceSubtypes,
					tenuresCar,
					tenuresDevice,
					defaultCarTenure,
					defaultDeviceTenure,
					isOnboardingConsentEnabled,
					isAddressConsentEnabled,
					restrictDeviceCtcView,
					financeChargesEnabled,
					showCtc,
					deliverToOfficeAddress,
					buybackConfig,
					fuelDriverMaintenance,
					availableInsuranceYears,
					insuranceRates,
					allowedAccessoryTypes,
					allowedAccessoryCount,
					homepageBannerCar,
					homepageBannerDevice,
				} = parseConfigData(
					programs,
					consent,
					typeof configRes.response.logo === "string"
						? configRes.response.logo
						: undefined,
				);

				// 3. Update tenure state (only after hydration)
				if (hasHydrated) {
					// Read the LATEST appData from the Zustand store directly.
					// appData.selectedCarTenure/selectedDeviceTenure in the closure above
					// may be stale: the effect closure is captured before the async
					// getConfig() call resolves. If URL sync (in ProductList) sets
					// tenure=9 DURING the fetch, the stale closure still sees undefined
					// and would overwrite the user's URL-loaded tenure with the default.
					const latestAppData = useAppStore.getState().data;

					const updates = calculateTenureUpdates(
						latestAppData.selectedCarTenure,
						latestAppData.selectedDeviceTenure,
						tenuresCar,
						tenuresDevice,
						defaultCarTenure,
						defaultDeviceTenure,
					);

					if (Object.keys(updates).length > 0) {
						setData(updates);
					}

					// Clear user-selected freebie if it was a company default and defaults are now gone (e.g. logout)
					const newDefaultFreebies = rawFreebieConfig?.defaultFreebies ?? [];
					const newDefaultIds = new Set(newDefaultFreebies.map((f) => f.id));
					if (
						newDefaultFreebies.length === 0 &&
						latestAppData.freebie &&
						(latestAppData.companyDefaultFreebies ?? []).some(
							(d) => d.id === latestAppData.freebie?.id,
						)
					) {
						setData({ freebie: null });
					}
					// If user's selected freebie became a company default, clear it (avoid duplication)
					if (
						latestAppData.freebie &&
						newDefaultIds.has(latestAppData.freebie.id)
					) {
						setData({ freebie: null });
					}

					// Keep onboardingCompletionType in sync with the freshly-resolved
					// showCar/showDevice - done here (atomically with the config that
					// determines these flags) rather than left to a consumer
					// component's own effect (e.g. Toggle), which depends on that
					// component being mounted at all and on its own render/effect
					// timing lining up with this update. Without this, a login that
					// flips a company from "both allowed" to "devices only" while the
					// user is already on the cars tab would hide the tab switcher but
					// leave the (now-inaccessible) cars tab's content on screen.
					if (showDevice && !showCar) {
						if (latestAppData.onboardingCompletionType !== "devices") {
							setData({ onboardingCompletionType: "devices" });
						}
					} else if (showCar && !showDevice) {
						if (latestAppData.onboardingCompletionType !== "cars") {
							setData({ onboardingCompletionType: "cars" });
						}
					}
				}

				// Reset insuranceYears if the currently-selected year is no longer available
				// (e.g. config changed from 3-year to 1-year plan)
				const latestInsuranceYears =
					useAppStore.getState().data.insuranceYears ?? 1;
				if (!availableInsuranceYears.includes(latestInsuranceYears)) {
					setData({ insuranceYears: availableInsuranceYears[0] ?? 1 });
				}

				// 4. Update cookieless data
				const configData = {
					showCar,
					showDevice,
					showToggle: showCar && showDevice,
					deviceSubtypes,
					defaultCarTenure,
					defaultDeviceTenure,
					tenuresCar,
					tenuresDevice,
					isOnboardingConsentEnabled,
					isAddressConsentEnabled,
					companyBanners: banners,
					restrictDeviceCtcView,
					financeChargesEnabled,
					showCtc,
					deliverToOfficeAddress,
					buybackConfig,
					fuelDriverMaintenance,
					companyDefaultFreebies: rawFreebieConfig?.defaultFreebies ?? [],
					freebieRules: rawFreebieConfig?.rules,
					availableInsuranceYears,
					insuranceRates,
					allowedAccessoryTypes,
					allowedAccessoryCount,
					homepageBannerCar,
					homepageBannerDevice,
				};
				// sharedConfigFetch is module-level and gets reassigned the moment a
				// newer auth state (e.g. a login that happened while this fetch was
				// still in flight) starts its own fetch - but this fetch's promise
				// keeps running regardless and would otherwise still apply its
				// (now-stale, wrong-user) result whenever it happens to resolve,
				// potentially overwriting the correct config that already landed.
				// Only apply this result if it's still the fetch the cache considers
				// current for this exact auth state.
				if (sharedConfigFetch?.authKey !== authKey) {
					clientLogger.info(
						"Discarding stale config response - auth state changed while this fetch was in flight",
					);
					return;
				}
				setCookielessData(configData);
				hasAnyInstanceFetchedSuccessfully = true;
			} catch (err) {
				clientLogger.error("Error in fetching config: ", err as Error);
				if (sharedConfigFetch?.authKey !== authKey) return;
				if (!hasAnyInstanceFetchedSuccessfully) {
					setCookielessData(getFallbackConfig());
				}
			}
		};

		// Reuse the in-flight/completed fetch for this auth state if one already
		// exists (started by this or another useConfig instance); otherwise this
		// instance becomes the one that actually fetches. Every instance - whether
		// it started the fetch or is just reusing it - marks itself ready once the
		// shared promise settles.
		if (!sharedConfigFetch || sharedConfigFetch.authKey !== authKey) {
			sharedConfigFetch = { authKey, promise: fetchConfig() };
		}
		sharedConfigFetch.promise.then(() => setIsConfigReady(true));
	}, [
		setCookielessData,
		setData,
		hasHydrated,
		appData?.selectedCarTenure,
		appData?.selectedDeviceTenure,
		appData?.authorization, // React to token changes (guest/login/logout)
		appData?.user, // React to resolved user state
	]);

	return {
		isConfigReady,
	};
};
